import { ChatUser } from '@equip.health/ui';

export const MESSAGES_PER_PAGE = 50;
export const CHANNELS_LIMIT = 50;

export const MARK_READ_DURATION = 3000;
export const FILE_SIZE_ERROR_DURATION = 5000;

export enum ChannelId {
  PT_ADMIT = 'PT_ADMIT',
  PT_DIETITIAN = 'PT_DIETITIAN',
  PT_MEDICAL = 'PT_MEDICAL',
  PT_PEER = 'PT_PEER',
  PT_PSYCH = 'PT_PSYCH',
  PT_THERAPIST = 'PT_THERAPIST',
  CCT = 'MAUD_CARETEAM',
  ACT = 'ACT',
  PR_AS = 'PR_AS',
  PR_CCT = 'PR_CCT',
  PR_DIETITIAN = 'PR_DIETITIAN',
  PR_FAMILY = 'PR_FAMILY',
  PR_MEDICAL = 'PR_MEDICAL',
  PR_PSYCH = 'PR_PSYCH',
  PR_THERAPIST = 'PR_THERAPIST',
  PT_CCT = 'PT_CCT',
  PT_PR_CCT = 'PT_PR_CCT',
  SUP_DIETITIAN = 'SUP_DIETITIAN',
  SUP_FAMILY = 'SUP_FAMILY',
  SUP_MEDICAL = 'SUP_MEDICAL',
  SUP_PSYCH = 'SUP_PSYCH',
  SUP_THERAPIST = 'SUP_THERAPIST',
}

export const MESSAGES_TITLE = 'Messages';
export const DEFAULT_ELEMENT_HEIGHT = 40;
export const ENTER_KEY = 'Enter';
export const NEW_LINE = '\n';

export const MEMBERS_TO_DISPLAY = 5;
export const MEMBERS_TO_DISPLAY_IN_REPLY = 2;

export const DEFAULT_REPLY_PLACEHOLDER = 'Type your reply here';
export const MENTION_PEOPLE_TO_NOTIFY_THEM_OF_YOUR_MESSAGE =
  '@mention people to notify them of your message';
export const TYPE_YOUR_MESSAGE_TO_THE_GROUP_HERE =
  'Type your message to the group here. You can use @mention to tag specific people';

export const DEFAULT_SUGGESTIONS_TITLE = 'Group members';

export const FILE_SIZE_LIMIT_MESSAGE = ' exceeds the default limit of 25 MB.';

export const MESSAGE_LIST_MARGIN = 40;

export const NAME_CONNECTOR = ' & ';

export const HERE_MEMBER: ChatUser = {
  nickname: 'here',
  userId: '-1',
};

export const NEW_MESSAGE = {
  allMentionedUserIds: [],
  channelUrl: '',
  createdAt: 0,
  data: '',
  hasThread: false,
  id: '',
  isAdminMessage: false,
  isFileMessage: false,
  isMentioned: false,
  isNewThread: false,
  isRead: false,
  mentionedUserIds: [],
  mentionedUsers: [],
  message: '',
  messageId: null,
  parentMessageId: -1,
  relativeDate: '',
  sbMessage: null,
  sender: null,
  thread: [],
  threadInfo: null,
  timestamp: '',
};

export const FROZEN = 'frozen';
export const UNFROZEN = 'unfrozen';
export const FROZEN_MESSAGE = 'This channel is no longer active';
export const UNFROZEN_MESSAGE = 'This channel is active';

export const INTERNAL_CHANNEL_URL_PREFIXES = [ChannelId.ACT, ChannelId.CCT];

// Temp addition for March 2023, Release
export const ALWAYS_ALLOWED_CHANNEL_LIST = [
  ChannelId.ACT,
  ChannelId.CCT,
  ChannelId.PT_ADMIT,
  ChannelId.PR_AS,
];

export const IN_TREATMENT_CHANNEL_LIST = [
  ChannelId.PR_CCT,
  ChannelId.PR_FAMILY,
  ChannelId.PR_THERAPIST,
  ChannelId.PR_DIETITIAN,
  ChannelId.PR_MEDICAL,
  ChannelId.PR_PSYCH,
  ChannelId.PT_CCT,
  ChannelId.PT_DIETITIAN,
  ChannelId.PT_PEER,
  ChannelId.PT_PR_CCT,
  ChannelId.PT_THERAPIST,
  ChannelId.PT_MEDICAL,
  ChannelId.PT_PSYCH,
  ChannelId.SUP_DIETITIAN,
  ChannelId.SUP_FAMILY,
  ChannelId.SUP_THERAPIST,
  ChannelId.SUP_MEDICAL,
  ChannelId.SUP_PSYCH,
];

export const CHAT_INPUT_ACCEPT = [
  '.jpg',
  '.jpeg',
  '.png',
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
];

// File Size Limit
export const CHAT_ATTACHMENT_SIZE_LIMIT = 25 * 1024;

export const RESTRICTED_USERS_FROM_TAGGING = ['support', 'patient'];

export const USER_ACTIONS = ['joined', 'left'];

export enum UserRoles {
  PATIENT = 'Patient',
  PROVIDER = 'Provider',
  PROXY = 'Proxy',
  SUPPORT = 'Support',
  UNKNOWN = 'Unknown',
}

export const SUPPORT_TAG = '#support';

export enum ChannelTypes {
  ADMISSIONS_TEAM = 'Admissions Team',
  CLINICAL_CARE_TEAM = 'Provider Team',
  OTHER = 'Other',
  PATIENT_AND_ADMISSIONS_SPECIALISTS = 'Patient + Admissions Specialist',
  PATIENT_AND_DIETITIANS = 'Patient + Dietitian',
  PATIENT_AND_PEER_MENTORS = 'Patient + Peer Mentor',
  PATIENT_AND_PROXY_TO_CARE_TEAM = 'Patient + Supports + Provider Team',
  PATIENT_AND_PROXY_TO_THERAPY_RD_MENTOR = 'Patient + Supports + Therapy, RD, Mentor Team',
  PATIENT_AND_PROXY_TO_THERAPY_RD_COACHING_TEAM = 'Patient + Supports + Therapy, RD, Coaching Team',
  PATIENT_AND_RECOVERY_COACHES = 'Patient + Recovery Coach',
  PATIENT_AND_THERAPISTS = 'Patient + Therapist',
  PATIENT_AND_MEDICAL_TEAM = 'Patient + Medical Team',
  PATIENT_AND_PSYCH_TEAM = 'Patient + Psychiatry Team',
  PATIENT_TO_CLINICAL_CARE_TEAM = 'Patient + Provider Team',
  PATIENT_TO_THERAPY_RD_MENTOR = 'Patient + Therapy, RD, Mentor Team',
  PATIENT_TO_THERAPY_RD_COACHING_TEAM = 'Patient + Therapy, RD, Coaching Team',
  PROXY_AND_ADMISSION_SPECIALISTS = 'Supports + Admissions Specialist',
  PROXY_AND_CAREGIVER_COACHES = 'Supports + Caregiver Coach',
  PROXY_AND_FAMILY_MENTORS = 'Supports + Family Mentor',
  PROXY_AND_THERAPISTS = 'Supports + Therapist',
  PROXY_AND_DIETITIANS = 'Supports + Dietitian',
  PROXY_AND_MEDICAL_TEAM = 'Supports + Medical Team',
  PROXY_AND_PSYCH_TEAM = 'Supports + Psychiatry Team',
  PROXY_TO_CLINICAL_CARE_TEAM = 'Supports + Provider Team',
  PROXY_TO_THERAPY_RD_COACHING_TEAM = 'Supports + Therapy, RD, Coaching Team',
  PROXY_TO_THERAPY_RD_MENTOR = 'Supports + Therapy, RD, Mentor Team',
  SUPPORT_AND_CAREGIVER_COACHES = '#support + Caregiver Coach',
  SUPPORT_AND_DIETITIANS = '#support + Dietitian',
  SUPPORT_AND_FAMILY_MENTORS = '#support + Family Mentor',
  SUPPORT_AND_THERAPISTS = '#support + Therapist',
  SUPPORT_AND_MEDICAL_TEAM = '#support + Medical Team',
  SUPPORT_AND_PSYCH_TEAM = '#support + Psychiatry Team',
  UNKNOWN = 'Unknown',
}

export const CHANNEL_CLASSIFICATION = [
  { type: ChannelTypes.CLINICAL_CARE_TEAM, url: ChannelId.CCT },
  { type: ChannelTypes.ADMISSIONS_TEAM, url: ChannelId.ACT },
  {
    isOneOnOne: true,
    type: ChannelTypes.PROXY_AND_FAMILY_MENTORS,
    url: ChannelId.PR_FAMILY,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PROXY_AND_THERAPISTS,
    url: ChannelId.PR_THERAPIST,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PROXY_AND_DIETITIANS,
    url: ChannelId.PR_DIETITIAN,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PROXY_AND_MEDICAL_TEAM,
    url: ChannelId.PR_MEDICAL,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PROXY_AND_PSYCH_TEAM,
    url: ChannelId.PR_PSYCH,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PATIENT_TO_CLINICAL_CARE_TEAM,
    url: ChannelId.PT_CCT,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PROXY_TO_CLINICAL_CARE_TEAM,
    url: ChannelId.PR_CCT,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PROXY_AND_ADMISSION_SPECIALISTS,
    url: ChannelId.PR_AS,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PATIENT_AND_PROXY_TO_CARE_TEAM,
    url: ChannelId.PT_PR_CCT,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PATIENT_AND_PEER_MENTORS,
    url: ChannelId.PT_PEER,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PATIENT_AND_THERAPISTS,
    url: ChannelId.PT_THERAPIST,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PATIENT_AND_DIETITIANS,
    url: ChannelId.PT_DIETITIAN,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PATIENT_AND_ADMISSIONS_SPECIALISTS,
    url: ChannelId.PT_ADMIT,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PATIENT_AND_MEDICAL_TEAM,
    url: ChannelId.PT_MEDICAL,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.PATIENT_AND_PSYCH_TEAM,
    url: ChannelId.PT_PSYCH,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.SUPPORT_AND_FAMILY_MENTORS,
    url: ChannelId.SUP_FAMILY,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.SUPPORT_AND_THERAPISTS,
    url: ChannelId.SUP_THERAPIST,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.SUPPORT_AND_DIETITIANS,
    url: ChannelId.SUP_DIETITIAN,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.SUPPORT_AND_MEDICAL_TEAM,
    url: ChannelId.SUP_MEDICAL,
  },
  {
    isOneOnOne: true,
    type: ChannelTypes.SUPPORT_AND_PSYCH_TEAM,
    url: ChannelId.SUP_PSYCH,
  },
];

export enum ChatDividerType {
  UNREAD_MESSAGE,
  DATE,
}

export const MIME_TYPES = {
  //   File Extension   MIME Type
  abs: 'audio/x-mpeg',
  ai: 'application/postscript',
  aif: 'audio/x-aiff',
  aifc: 'audio/x-aiff',
  aiff: 'audio/x-aiff',
  aim: 'application/x-aim',
  anx: 'application/annodex',
  art: 'image/x-jg',
  asf: 'video/x-ms-asf',
  asx: 'video/x-ms-asf',
  au: 'audio/basic',
  avi: 'video/x-msvideo',
  avx: 'video/x-rad-screenplay',
  axa: 'audio/annodex',
  axv: 'video/annodex',
  bcpio: 'application/x-bcpio',
  bin: 'application/octet-stream',
  bmp: 'image/bmp',
  body: 'text/html',
  cdf: 'application/x-cdf',
  cer: 'application/pkix-cert',
  class: 'application/java',
  cpio: 'application/x-cpio',
  csh: 'application/x-csh',
  css: 'text/css',
  dib: 'image/bmp',
  doc: 'application/msword',
  dtd: 'application/xml-dtd',
  dv: 'video/x-dv',
  dvi: 'application/x-dvi',
  eot: 'application/vnd.ms-fontobject',
  eps: 'application/postscript',
  etx: 'text/x-setext',
  exe: 'application/octet-stream',
  flac: 'audio/flac',
  gif: 'image/gif',
  gtar: 'application/x-gtar',
  gz: 'application/x-gzip',
  hdf: 'application/x-hdf',
  hqx: 'application/mac-binhex40',
  htc: 'text/x-component',
  htm: 'text/html',
  html: 'text/html',
  ief: 'image/ief',
  jad: 'text/vnd.sun.j2me.app-descriptor',
  jar: 'application/java-archive',
  java: 'text/x-java-source',
  jnlp: 'application/x-java-jnlp-file',
  jpe: 'image/jpeg',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  js: 'application/javascript',
  jsf: 'text/plain',
  json: 'application/json',
  jspf: 'text/plain',
  kar: 'audio/midi',
  latex: 'application/x-latex',
  m3u: 'audio/x-mpegurl',
  mac: 'image/x-macpaint',
  man: 'text/troff',
  mathml: 'application/mathml+xml',
  me: 'text/troff',
  mid: 'audio/midi',
  midi: 'audio/midi',
  mif: 'application/x-mif',
  mov: 'video/quicktime',
  movie: 'video/x-sgi-movie',
  mp1: 'audio/mpeg',
  mp2: 'audio/mpeg',
  mp3: 'audio/mpeg',
  mp4: 'video/mp4',
  mpa: 'audio/mpeg',
  mpe: 'video/mpeg',
  mpeg: 'video/mpeg',
  mpega: 'audio/x-mpeg',
  mpg: 'video/mpeg',
  mpv2: 'video/mpeg2',
  ms: 'application/x-wais-source',
  nc: 'application/x-netcdf',
  oda: 'application/oda',
  odb: 'application/vnd.oasis.opendocument.database',
  odc: 'application/vnd.oasis.opendocument.chart',
  odf: 'application/vnd.oasis.opendocument.formula',
  odg: 'application/vnd.oasis.opendocument.graphics',
  odi: 'application/vnd.oasis.opendocument.image',
  odm: 'application/vnd.oasis.opendocument.text-master',
  odp: 'application/vnd.oasis.opendocument.presentation',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  odt: 'application/vnd.oasis.opendocument.text',
  oga: 'audio/ogg',
  ogg: 'audio/ogg',
  ogv: 'video/ogg',
  ogx: 'application/ogg',
  otf: 'application/x-font-opentype',
  otg: 'application/vnd.oasis.opendocument.graphics-template',
  oth: 'application/vnd.oasis.opendocument.text-web',
  otp: 'application/vnd.oasis.opendocument.presentation-template',
  ots: 'application/vnd.oasis.opendocument.spreadsheet-template',
  ott: 'application/vnd.oasis.opendocument.text-template',
  pbm: 'image/x-portable-bitmap',
  pct: 'image/pict',
  pdf: 'application/pdf',
  pgm: 'image/x-portable-graymap',
  pic: 'image/pict',
  pict: 'image/pict',
  pls: 'audio/x-scpls',
  png: 'image/png',
  pnm: 'image/x-portable-anymap',
  pnt: 'image/x-macpaint',
  ppm: 'image/x-portable-pixmap',
  pps: 'application/vnd.ms-powerpoint',
  ppt: 'application/vnd.ms-powerpoint',
  ps: 'application/postscript',
  psd: 'image/vnd.adobe.photoshop',
  qt: 'video/quicktime',
  qti: 'image/x-quicktime',
  qtif: 'image/x-quicktime',
  ras: 'image/x-cmu-raster',
  rdf: 'application/rdf+xml',
  rgb: 'image/x-rgb',
  rm: 'application/vnd.rn-realmedia',
  roff: 'text/troff',
  rtf: 'application/rtf',
  rtx: 'text/richtext',
  sfnt: 'application/font-sfnt',
  sh: 'application/x-sh',
  shar: 'application/x-shar',
  sit: 'application/x-stuffit',
  snd: 'audio/basic',
  spx: 'audio/ogg',
  src: 'application/x-wais-source',
  sv4cpio: 'application/x-sv4cpio',
  sv4crc: 'application/x-sv4crc',
  svg: 'image/svg+xml',
  svgz: 'image/svg+xml',
  swf: 'application/x-shockwave-flash',
  t: 'text/troff',
  tar: 'application/x-tar',
  tcl: 'application/x-tcl',
  tex: 'application/x-tex',
  texi: 'application/x-texinfo',
  texinfo: 'application/x-texinfo',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  tr: 'text/troff',
  tsv: 'text/tab-separated-values',
  ttf: 'application/x-font-ttf',
  txt: 'text/plain',
  ulw: 'audio/basic',
  ustar: 'application/x-ustar',
  vsd: 'application/vnd.visio',
  vxml: 'application/voicexml+xml',
  wav: 'audio/x-wav',
  wbmp: 'image/vnd.wap.wbmp',
  wml: 'text/vnd.wap.wml',
  wmlc: 'application/vnd.wap.wmlc',
  wmls: 'text/vnd.wap.wmlsc',
  wmlscriptc: 'application/vnd.wap.wmlscriptc',
  wmv: 'video/x-ms-wmv',
  woff: 'application/font-woff',
  woff2: 'application/font-woff2',
  wrl: 'model/vrml',
  wspolicy: 'application/wspolicy+xml',
  xbm: 'image/x-xbitmap',
  xht: 'application/xhtml+xml',
  xhtml: 'application/xhtml+xml',
  xls: 'application/vnd.ms-excel',
  xml: 'application/xml',
  xpm: 'image/x-xpixmap',
  xsl: 'application/xml',
  xslt: 'application/xslt+xml',
  xspf: 'application/xspf+xml',
  xul: 'application/vnd.mozilla.xul+xml',
  xwd: 'image/x-xwindowdump',
  z: 'application/x-compress',
  zip: 'application/zip',
};

export const MENTOR_ROLE_EVOLUTION_CHANNEL_NAMES: Partial<
  Record<ChannelTypes, ChannelTypes>
> = {
  [ChannelTypes.PROXY_AND_FAMILY_MENTORS]:
    ChannelTypes.PROXY_AND_CAREGIVER_COACHES,

  [ChannelTypes.PATIENT_AND_PEER_MENTORS]:
    ChannelTypes.PATIENT_AND_RECOVERY_COACHES,

  [ChannelTypes.SUPPORT_AND_FAMILY_MENTORS]:
    ChannelTypes.SUPPORT_AND_CAREGIVER_COACHES,

  [ChannelTypes.PATIENT_TO_THERAPY_RD_MENTOR]:
    ChannelTypes.PATIENT_TO_THERAPY_RD_COACHING_TEAM,

  [ChannelTypes.PROXY_TO_THERAPY_RD_MENTOR]:
    ChannelTypes.PROXY_TO_THERAPY_RD_COACHING_TEAM,

  [ChannelTypes.PATIENT_AND_PROXY_TO_THERAPY_RD_MENTOR]:
    ChannelTypes.PATIENT_AND_PROXY_TO_THERAPY_RD_COACHING_TEAM,
};
